import React, { useState, useEffect, useRef } from "react";
import "./Shawls.css";
import { FaPlus, FaMinus, FaTimes, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import base_url from "../../api";
import { useNavigate } from "react-router-dom";

// Import shawllooks images
import shawlLook1 from "../../assets/shawllooks/1.jpg";
import shawlLook2 from "../../assets/shawllooks/2.jpg";
import shawlLook3 from "../../assets/shawllooks/3.jpg";
import shawlLook4 from "../../assets/shawllooks/4.jpg";
import shawlLook5 from "../../assets/shawllooks/5.jpg";
import shawlLook6 from "../../assets/shawllooks/6.jpg";
import shawlLook7 from "../../assets/shawllooks/7.jpg";
import shawlLook8 from "../../assets/shawllooks/8.jpg";
import shawlLook9 from "../../assets/shawllooks/9.jpg";
import shawlLook10 from "../../assets/shawllooks/10.jpg";

// Import shawls images
import shawl1 from "../../assets/shawls/1.png";
import shawl2 from "../../assets/shawls/2.png";
import shawl3 from "../../assets/shawls/3.png";
import shawl4 from "../../assets/shawls/4.png";
import shawl5 from "../../assets/shawls/5.png";
import shawl6 from "../../assets/shawls/6.png";
import shawl7 from "../../assets/shawls/7.png";
import shawl8 from "../../assets/shawls/8.png";
import shawl9 from "../../assets/shawls/9.png";
import shawl10 from "../../assets/shawls/10.png";

// Create arrays for matching images
const shawlLooks = [
  shawlLook1, shawlLook2, shawlLook3, shawlLook4, shawlLook5,
  shawlLook6, shawlLook7, shawlLook8, shawlLook9, shawlLook10
];

const shawls = [
  shawl1, shawl2, shawl3, shawl4, shawl5,
  shawl6, shawl7, shawl8, shawl9, shawl10
];

// Create carousel images array - matching images by index
const createCarouselImages = () => {
  const carouselData = [];
  const maxLength = Math.max(shawlLooks.length, shawls.length);
  
  for (let i = 0; i < maxLength; i++) {
    const images = [];
    if (shawlLooks[i]) images.push(shawlLooks[i]);
    if (shawls[i]) images.push(shawls[i]);
    if (images.length > 0) {
      carouselData.push(images);
    }
  }
  
  return carouselData;
};

const carouselImages = createCarouselImages();

// For card display, use shawllooks
const shawlImages = shawlLooks;

const PRICE = 33.99;
const SHIPPING_CHARGES = 35.00;
const TAX_RATE = 0.13; // 13% tax rate

// Countries where we show EUR (price stays the same; only currency label changes)
const EUROPEAN_COUNTRIES = [
  "United Kingdom", "Germany", "France", "Italy", "Spain", "Netherlands",
  "Belgium", "Switzerland", "Poland", "Sweden", "Norway", "Denmark",
  "Finland", "Ireland", "Portugal", "Greece", "Austria", "Czech Republic",
  "Hungary", "Romania", "Russia", "Turkey"
];

const getCurrencyForCountry = (country) => {
  if (country === "Canada") return "CAD";
  if (EUROPEAN_COUNTRIES.includes(country)) return "EUR";
  if (!country) return "CAD";
  return "USD";
};

const getCurrencySymbol = (currencyCode) => {
  switch (currencyCode) {
    case "CAD":
      return "C$";
    case "EUR":
      return "€";
    case "USD":
      return "$";
    default:
      return "";
  }
};

const Shawls = () => {
  const navigate = useNavigate();
  const [quantities, setQuantities] = useState(
    Array(shawlImages.length).fill(0)
  );
  const [selectedShawlIndex, setSelectedShawlIndex] = useState(null);
  const [popupQuantity, setPopupQuantity] = useState(1);
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);
  const prevHasItemsRef = useRef(false);

  // Address form state
  const [address, setAddress] = useState({
    country: "",
    state: "",
    city: "",
    houseNo: "",
    street: "",
    landmark: "",
    contact: "",
    currency: "CAD",
  });
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [addressErrors, setAddressErrors] = useState({});

  const currencyCode = getCurrencyForCountry(address.country);
  const currencySymbol = getCurrencySymbol(currencyCode);

  // Calculate order totals
  const subtotal = quantities.reduce((sum, qty) => sum + qty * PRICE, 0);
  const taxAmount = subtotal * TAX_RATE;
  const total = subtotal + taxAmount + SHIPPING_CHARGES;
  const hasItems = quantities.some(qty => qty > 0);

  // Fetch countries on component mount
  useEffect(() => {
    fetch("https://countriesnow.space/api/v0.1/countries/states")
      .then((res) => res.json())
      .then((data) => {
        if (data.data && Array.isArray(data.data)) {
          setCountries(data.data);
        }
      })
      .catch((err) => {
        console.error("Error fetching countries:", err);
      });
  }, []);

  // Show toast when order summary appears
  useEffect(() => {
    if (hasItems && !prevHasItemsRef.current) {
      toast.info("View order summary below", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
    prevHasItemsRef.current = hasItems;
  }, [hasItems]);

  const increaseQty = (index) => {
    const updated = [...quantities];
    updated[index] += 1;
    setQuantities(updated);
  };

  const decreaseQty = (index) => {
    const updated = [...quantities];
    if (updated[index] > 0) updated[index] -= 1;
    setQuantities(updated);
  };

  const handleCardClick = (index) => {
    setSelectedShawlIndex(index);
    setPopupQuantity(quantities[index] > 0 ? quantities[index] : 1);
    setCurrentCarouselIndex(0); // Reset carousel to first image
  };

  const closePopup = () => {
    setSelectedShawlIndex(null);
    setPopupQuantity(1);
    setCurrentCarouselIndex(0);
  };

  const nextCarouselImage = () => {
    if (selectedShawlIndex !== null) {
      const images = carouselImages[selectedShawlIndex];
      if (images && images.length > 0) {
        setCurrentCarouselIndex((prev) => (prev + 1) % images.length);
      }
    }
  };

  const prevCarouselImage = () => {
    if (selectedShawlIndex !== null) {
      const images = carouselImages[selectedShawlIndex];
      if (images && images.length > 0) {
        setCurrentCarouselIndex((prev) => (prev - 1 + images.length) % images.length);
      }
    }
  };

  const goToCarouselImage = (index) => {
    setCurrentCarouselIndex(index);
  };

  const handlePopupIncreaseQty = () => {
    setPopupQuantity(prev => prev + 1);
  };

  const handlePopupDecreaseQty = () => {
    if (popupQuantity > 1) {
      setPopupQuantity(prev => prev - 1);
    }
  };

  const handleAddToCart = () => {
    if (selectedShawlIndex !== null) {
      const updated = [...quantities];
      updated[selectedShawlIndex] = popupQuantity;
      setQuantities(updated);
      toast.success(`Added ${popupQuantity} item(s) to cart`, {
        position: "top-right",
        autoClose: 2000,
      });
      closePopup();
    }
  };

  // Address form handlers
  const handleAddressChange = (field, value) => {
    setAddress((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field
    if (addressErrors[field]) {
      setAddressErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleCountryChange = (countryName) => {
    handleAddressChange("country", countryName);
    handleAddressChange("state", "");
    handleAddressChange("city", "");
    setCities([]);
    // Auto-set currency based on selected country
    handleAddressChange("currency", getCurrencyForCountry(countryName));

    // Load states for selected country
    const countryData = countries.find((c) => c.name === countryName);
    if (countryData) {
      setStates(countryData.states || []);
    } else {
      setStates([]);
    }
  };

  const handleStateChange = (stateName) => {
    handleAddressChange("state", stateName);
    handleAddressChange("city", "");

    if (stateName && address.country) {
      fetch("https://countriesnow.space/api/v0.1/countries/state/cities", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ country: address.country, state: stateName }),
      })
        .then((res) => res.json())
        .then((data) => {
          setCities(Array.isArray(data.data) ? data.data : []);
        })
        .catch((err) => {
          console.error("Error fetching cities:", err);
          setCities([]);
        });
    } else {
      setCities([]);
    }
  };

  const placeOrder = async () => {
    // Check if user is logged in
    const user = localStorage.getItem("user");
    if (!user) {
      toast.error("Please login to place an order", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    try {
      const userData = JSON.parse(user);
      const userEmail = userData.email;

      if (!userEmail) {
        toast.error("User email not found. Please login again.", {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }

      // Prepare order items
      const orderItems = quantities
        .map((qty, index) => ({
          shawlId: index + 1,
          quantity: qty,
          price: PRICE
        }))
        .filter(item => item.quantity > 0);

      if (orderItems.length === 0) {
        toast.error("Please add items to your cart before placing an order", {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }

      // Validate address fields
      const newErrors = {};
      if (!address.country) newErrors.country = "Country is required";
      if (!address.state) newErrors.state = "State is required";
      if (!address.city) newErrors.city = "City is required";
      if (!address.houseNo) newErrors.houseNo = "House number is required";
      if (!address.contact) newErrors.contact = "Contact number is required";

      if (Object.keys(newErrors).length > 0) {
        setAddressErrors(newErrors);
        toast.error("Please fill in all required address fields", {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }

      // Show loading toast
      toast.info("Placing your order...", {
        position: "top-right",
        autoClose: 2000,
      });

      // Call API to create shawl order
      const response = await fetch(`${base_url}/shawl-orders`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          items: orderItems,
          userEmail: userEmail,
          subtotal: subtotal,
          taxAmount: taxAmount,
          shippingCharges: SHIPPING_CHARGES,
          total: total,
          // Include address fields
          country: address.country,
          state: address.state,
          city: address.city,
          houseNo: address.houseNo,
          street: address.street || "",
          landmark: address.landmark || "",
          contact: address.contact,
          // Save currency code in DB (CAD/EUR/USD) based on selected country
          currency: currencyCode,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const emailNote = data.emailSent
          ? ` Payment email sent to ${userEmail}.`
          : data.emailError
            ? ` Email issue: ${data.emailError}`
            : " Payment email may be delayed.";

        toast.success(`Order placed successfully! Order #${data.orderId}.${emailNote}`, {
          position: "top-right",
          autoClose: 5000,
        });

        // Reset quantities and address after successful order
        setQuantities(Array(shawlImages.length).fill(0));
        setAddress({
          country: "",
          state: "",
          city: "",
          houseNo: "",
          street: "",
          landmark: "",
          contact: "",
          currency: "CAD",
        });
        setAddressErrors({});
        setStates([]);
        setCities([]);

        // Redirect user to profile Orders tab with this order highlighted
        if (data.orderId) {
          navigate(`/profile?tab=orders&orderNo=${data.orderId}`);
        } else {
          navigate(`/profile?tab=orders`);
        }
      } else {
        toast.error(data.message || "Failed to place order. Please try again.", {
          position: "top-right",
          autoClose: 4000,
        });
      }
    } catch (error) {
      console.error("Error placing order:", error);
      toast.error("An error occurred while placing your order. Please try again.", {
        position: "top-right",
        autoClose: 4000,
      });
    }
  };

  return (
    <div className="shawls-container">
      <div className="shawls-header">
        <h1>Shawls</h1>
        <p className="shawls-subtitle">Discover our collection of premium shawls</p>
      </div>

      <div className="shawls-main-layout">
        <div className="shawls-grid-wrapper">
          <div className="shawls-grid">
            {shawlImages.map((img, index) => (
              <div 
                key={index} 
                className="shawl-card"
                onClick={() => handleCardClick(index)}
              >
                <div className="shawl-image-container">
                  <img
                    src={img}
                    alt={`Premium Kashmiri Shawl `}
                    className="shawl-image"
                  />
                </div>
                
                <div className="shawl-info">
                  <p className="shawl-price">{currencySymbol}{PRICE.toFixed(2)} {currencyCode}</p>
                  
                  <div className="shawl-actions">
                    <div 
                      className="quantity-selector"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        className="quantity-btn"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          decreaseQty(index);
                        }}
                        disabled={quantities[index] <= 0}
                      >
                        <FaMinus />
                      </button>
                      <span className="quantity-value">{quantities[index]}</span>
                      <button
                        className="quantity-btn"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          increaseQty(index);
                        }}
                      >
                        <FaPlus />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Order Summary */}
        <div className="order-summary-section">
          <h2 className="order-summary-title">Order Summary</h2>
          
          <div className="order-summary-details">
            {/* Pricing Breakdown */}
            <div className="pricing-breakdown">
              {/* Individual shawl items */}
              {hasItems ? (
                quantities.map((qty, index) =>
                  qty > 0 ? (
                    <div key={index} className="pricing-row look-price-row">
                      <div className="pricing-row-item">
                        <img 
                          src={shawlImages[index]} 
                          alt={`Shawl ${index + 1}`}
                          className="order-summary-shawl-image"
                        />
                        <span>({qty} qty × {currencySymbol}{PRICE.toFixed(2)} {currencyCode})</span>
                      </div>
                      <span>{currencySymbol}{(qty * PRICE).toFixed(2)} {currencyCode}</span>
                    </div>
                  ) : null
                )
              ) : (
                <div className="pricing-row" style={{ padding: "10px 0", color: "#6c757d", fontStyle: "italic" }}>
                  <span>No items added yet</span>
                </div>
              )}
              
              {/* Subtotal */}
              <div className="pricing-row pricing-subtotal">
                <span>Subtotal:</span>
                <span>{currencySymbol}{subtotal.toFixed(2)} {currencyCode}</span>
              </div>
              
              {/* Tax */}
              <div className="pricing-row" style={{ paddingLeft: "16px", fontSize: "13px", color: "#6c757d" }}>
                <span>Tax (HST/GST) ({(TAX_RATE * 100).toFixed(0)}%):</span>
                <span>{currencySymbol}{taxAmount.toFixed(2)} {currencyCode}</span>
              </div>
              
              {/* Shipping */}
              <div className="pricing-row pricing-shipping">
                <span>Shipping Charges:</span>
                <span>{currencySymbol}{SHIPPING_CHARGES.toFixed(2)} {currencyCode}</span>
              </div>
              
              {/* Total */}
              <div className="pricing-row pricing-total">
                <span><strong>Total Amount ({currencyCode}):</strong></span>
                <span><strong>{currencySymbol}{total.toFixed(2)} {currencyCode}</strong></span>
              </div>
            </div>
          </div>

          {/* Delivery Address Form */}
          {hasItems && (
            <div className="address-form-section" style={{ 
              marginTop: "20px", 
              padding: "20px", 
              background: "#f8f9fa", 
              borderRadius: "8px",
              border: "1px solid #dee2e6"
            }}>
              <h3 style={{ marginTop: 0, marginBottom: "15px", fontSize: "18px", color: "#333" }}>
                Delivery Address
              </h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "15px" }}>
                {/* Country */}
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={{ display: "block", marginBottom: "5px", fontWeight: "600", fontSize: "14px" }}>
                    Country <span style={{ color: "red" }}>*</span>
                  </label>
                  <select
                    value={address.country}
                    onChange={(e) => handleCountryChange(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: addressErrors.country ? "2px solid #dc3545" : "1px solid #ddd",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  >
                    <option value="">Select Country</option>
                    {countries.map((c, idx) => (
                      <option key={`${c.name}-${idx}`} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  {addressErrors.country && (
                    <span style={{ color: "#dc3545", fontSize: "12px", marginTop: "5px", display: "block" }}>
                      {addressErrors.country}
                    </span>
                  )}
                </div>

                {/* State */}
                <div>
                  <label style={{ display: "block", marginBottom: "5px", fontWeight: "600", fontSize: "14px" }}>
                    State <span style={{ color: "red" }}>*</span>
                  </label>
                  <select
                    value={address.state}
                    onChange={(e) => handleStateChange(e.target.value)}
                    disabled={!address.country}
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: addressErrors.state ? "2px solid #dc3545" : "1px solid #ddd",
                      borderRadius: "4px",
                      fontSize: "14px",
                      opacity: address.country ? 1 : 0.6
                    }}
                  >
                    <option value="">Select State</option>
                    {states.map((s, idx) => (
                      <option key={`${s.name}-${idx}`} value={s.name}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                  {addressErrors.state && (
                    <span style={{ color: "#dc3545", fontSize: "12px", marginTop: "5px", display: "block" }}>
                      {addressErrors.state}
                    </span>
                  )}
                </div>

                {/* City */}
                <div>
                  <label style={{ display: "block", marginBottom: "5px", fontWeight: "600", fontSize: "14px" }}>
                    City <span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={address.city}
                    onChange={(e) => handleAddressChange("city", e.target.value)}
                    placeholder="Enter city"
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: addressErrors.city ? "2px solid #dc3545" : "1px solid #ddd",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                  {addressErrors.city && (
                    <span style={{ color: "#dc3545", fontSize: "12px", marginTop: "5px", display: "block" }}>
                      {addressErrors.city}
                    </span>
                  )}
                </div>

                {/* House Number */}
                <div>
                  <label style={{ display: "block", marginBottom: "5px", fontWeight: "600", fontSize: "14px" }}>
                    House Number <span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={address.houseNo}
                    onChange={(e) => handleAddressChange("houseNo", e.target.value)}
                    placeholder="House/Apartment Number"
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: addressErrors.houseNo ? "2px solid #dc3545" : "1px solid #ddd",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                  {addressErrors.houseNo && (
                    <span style={{ color: "#dc3545", fontSize: "12px", marginTop: "5px", display: "block" }}>
                      {addressErrors.houseNo}
                    </span>
                  )}
                </div>

                {/* Street */}
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={{ display: "block", marginBottom: "5px", fontWeight: "600", fontSize: "14px" }}>
                    Street Address
                  </label>
                  <input
                    type="text"
                    value={address.street}
                    onChange={(e) => handleAddressChange("street", e.target.value)}
                    placeholder="Street Address"
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                </div>

                {/* Landmark */}
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={{ display: "block", marginBottom: "5px", fontWeight: "600", fontSize: "14px" }}>
                    Landmark (Optional)
                  </label>
                  <input
                    type="text"
                    value={address.landmark}
                    onChange={(e) => handleAddressChange("landmark", e.target.value)}
                    placeholder="Nearby Landmark"
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                </div>

                {/* Contact */}
                <div>
                  <label style={{ display: "block", marginBottom: "5px", fontWeight: "600", fontSize: "14px" }}>
                    Contact Number <span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    type="tel"
                    value={address.contact}
                    onChange={(e) => handleAddressChange("contact", e.target.value)}
                    placeholder="Phone Number"
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: addressErrors.contact ? "2px solid #dc3545" : "1px solid #ddd",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                  {addressErrors.contact && (
                    <span style={{ color: "#dc3545", fontSize: "12px", marginTop: "5px", display: "block" }}>
                      {addressErrors.contact}
                    </span>
                  )}
                </div>

                {/* Currency (auto-set from country) */}
                <div>
                  <label style={{ display: "block", marginBottom: "5px", fontWeight: "600", fontSize: "14px" }}>
                    Currency (auto)
                  </label>
                  <input
                    type="text"
                    value={`${currencyCode} (${currencySymbol})`}
                    readOnly
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      fontSize: "14px",
                      background: "#e9ecef"
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          <button
            className="place-order-btn"
            onClick={placeOrder}
            disabled={!hasItems}
            style={{ opacity: hasItems ? 1 : 0.5, cursor: hasItems ? 'pointer' : 'not-allowed', marginTop: "20px" }}
          >
            Place Order
          </button>
        </div>
      </div>

      {/* Popup Modal */}
      {selectedShawlIndex !== null && (
        <div className="shawl-popup-overlay" onClick={closePopup}>
          <div className="shawl-popup-content" onClick={(e) => e.stopPropagation()}>
            <button className="shawl-popup-close" onClick={closePopup}>
              <FaTimes />
            </button>
            
            <div className="shawl-popup-body">
              <div className="shawl-popup-image-container">
                {carouselImages[selectedShawlIndex] && carouselImages[selectedShawlIndex].length > 0 ? (
                  <>
                    <div className="shawl-carousel-wrapper">
                      <div className="carousel-images-container">
                        {carouselImages[selectedShawlIndex].map((img, imgIndex) => (
                          <img
                            key={imgIndex}
                            src={img}
                            alt={`Premium Kashmiri Shawl ${selectedShawlIndex + 1} - Image ${imgIndex + 1}`}
                            className={`shawl-popup-image ${imgIndex === currentCarouselIndex ? 'active' : ''}`}
                          />
                        ))}
                      </div>
                      
                      {carouselImages[selectedShawlIndex].length > 1 && (
                        <>
                          <button
                            className="carousel-nav-btn carousel-prev"
                            onClick={(e) => {
                              e.stopPropagation();
                              prevCarouselImage();
                            }}
                            type="button"
                          >
                            <FaChevronLeft />
                          </button>
                          <button
                            className="carousel-nav-btn carousel-next"
                            onClick={(e) => {
                              e.stopPropagation();
                              nextCarouselImage();
                            }}
                            type="button"
                          >
                            <FaChevronRight />
                          </button>
                          
                          <div className="carousel-dots">
                            {carouselImages[selectedShawlIndex].map((_, dotIndex) => (
                              <button
                                key={dotIndex}
                                className={`carousel-dot ${dotIndex === currentCarouselIndex ? 'active' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  goToCarouselImage(dotIndex);
                                }}
                                type="button"
                                aria-label={`Go to image ${dotIndex + 1}`}
                              />
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </>
                ) : (
                  <img
                    src={shawlImages[selectedShawlIndex]}
                    alt={`Premium Kashmiri Shawl ${selectedShawlIndex + 1}`}
                    className="shawl-popup-image"
                  />
                )}
              </div>
              
              <div className="shawl-popup-info">
                <h3 className="shawl-popup-title">Premium Kashmiri Shawl</h3>
                <p className="shawl-popup-price">C${PRICE.toFixed(2)}</p>
                
                <div className="shawl-popup-quantity">
                  <label>Quantity:</label>
                  <div className="quantity-selector-popup">
                    <button
                      className="quantity-btn-popup"
                      type="button"
                      onClick={handlePopupDecreaseQty}
                      disabled={popupQuantity <= 1}
                    >
                      <FaMinus />
                    </button>
                    <span className="quantity-value-popup">{popupQuantity}</span>
                    <button
                      className="quantity-btn-popup"
                      type="button"
                      onClick={handlePopupIncreaseQty}
                    >
                      <FaPlus />
                    </button>
                  </div>
                </div>
                
                <button
                  className="add-to-cart-popup-btn"
                  onClick={handleAddToCart}
                >
                  Add to Cart
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default Shawls;